import { Component, EventEmitter, Output, Input } from '@angular/core';
import { UploadService } from 'src/app/private/services/upload.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-drag-drop-file-uploader',
  templateUrl: './drag-drop-file-uploader.component.html',
  styleUrls: ['./drag-drop-file-uploader.component.scss']
})
export class DragDropFileUploaderComponent {

  files: any[] = [];
  uploading = false;

  @Output() uploaded = new EventEmitter<any[]>();
  @Input() additionalData: any = {};
  @Input() moduleName: string = 'bugs';
  
  constructor(
    private uploadService: UploadService,
    private localStorage: LocalStorageService
  ) {}

  // -----------------------------
  // FILE SELECT
  // -----------------------------
  onFileSelect(event: any) {
    const selected = Array.from(event.target.files) as File[];
    this.prepareFiles(selected);
    // Reset input so same file can be selected again
    event.target.value = '';
  }

  // -----------------------------
  // DRAG & DROP
  // -----------------------------
  onDrop(event: DragEvent) {
    event.preventDefault();
    const dropped = Array.from(event.dataTransfer!.files) as File[];
    this.prepareFiles(dropped);
  }

  onDragOver(event: any) { event.preventDefault(); }
  onDragLeave(event: any) { event.preventDefault(); }

  // -----------------------------
  // PREVIEW SETUP
  // -----------------------------
  prepareFiles(fileList: File[]) {
    for (let file of fileList) {

      const fileObj: any = {
        file: file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview: '',
        uploading: false,
        uploaded: false,
        progress: 0,
        error: false
      };

      // PREVIEW FOR IMAGES
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: any) => fileObj.preview = e.target.result;
        reader.readAsDataURL(file);
      }

      this.files.push(fileObj);
    }
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  // -----------------------------
  // UPLOAD ALL FILES (with progress)
  // -----------------------------
  async uploadAll() {
    if (this.files.length === 0) return;

    this.uploading = true;
    const output: any[] = [];

    for (let i = 0; i < this.files.length; i++) {
      const f = this.files[i];

      // Skip already uploaded files
      if (f.uploaded === true) continue;

      f.uploading = true;
      f.progress = 0;

      const client_id = this.localStorage.getClientId();
      const formData = new FormData();

      formData.append("file", f.file);
      formData.append("folders", `crm/project/${client_id + '_' + this.additionalData.project_id}/${this.moduleName}/attachments`);
      formData.append("bucket", "global-upload-storage");

      try {
        // Upload with progress tracking
        const response: any = await this.uploadFileWithProgress(formData, f);

        output.push({
          key: response.key || null,
          url: response.url,
          file_name: f.name,
          name: f.name,
          type: f.type,
          size: f.size
        });

        f.progress = 100;
        f.uploaded = true;
        f.uploading = false;

        // Remove file from UI after 500ms delay (so user sees 100% completion)
        setTimeout(() => {
          const index = this.files.indexOf(f);
          if (index > -1) {
            this.files.splice(index, 1);
          }
        }, 500);

      } catch (err) {
        console.error('Upload error:', err);
        f.error = true;
        f.uploading = false;
        f.progress = 0;
      }
    }

    this.uploading = false;

    // Emit uploaded files
    if (output.length > 0) {
      this.uploaded.emit(output);
    }
  }

  // -----------------------------
  // UPLOAD WITH PROGRESS TRACKING
  // -----------------------------
  uploadFileWithProgress(formData: FormData, fileObj: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.uploadService.uploadDocumentsWithProgress(formData).subscribe({
        next: (event: any) => {
          if (event.type === 'progress') {
            // Update progress
            fileObj.progress = event.progress;
          } else if (event.type === 'response') {
            // Upload complete
            resolve(event.body);
          }
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  // Fallback method if progress tracking not available
  uploadFile(formData: FormData): Promise<any> {
    return new Promise((resolve, reject) => {
      this.uploadService.uploadDcouments(formData).subscribe({
        next: (res) => resolve(res),
        error: (err) => reject(err)
      });
    });
  }
}
